<script setup lang="ts">
import { defineEmits, defineProps, ref, watch, withDefaults } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { XIcon } from '@heroicons/vue/outline'

interface Props {
    modelValue: boolean
    showClose?: boolean
    size?: 'sm' | 'md' | 'lg'
    fullscreen?: boolean
    closeOnOutsideClick: boolean
}
const props = withDefaults(defineProps<Props>(), {
    showClose: true,
    size: 'md',
    fullscreen: false,
    closeOnOutsideClick: true,
})

const emits = defineEmits(['update:modelValue'])

const open = ref(props.modelValue)
watch(
    () => props.modelValue,
    (val) => (open.value = val)
)
watch(open, (val) => emits('update:modelValue', val))
</script>

<template>
    <TransitionRoot as="template" :show="open">
        <Dialog
            as="div"
            class="fixed inset-0 z-[3000] overflow-y-auto"
            @close="props.closeOnOutsideClick ? (open = false) : null">
            <div class="flex min-h-screen items-center justify-center px-4 pb-20 pt-4 text-center sm:block sm:p-0">
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100"
                    leave-to="opacity-0">
                    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <!-- This element is to trick the browser into centering the modal contents. -->
                <span class="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <TransitionChild
                    as="template"
                    enter="ease-out duration-300"
                    enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leave-from="opacity-100 translate-y-0 sm:scale-100"
                    leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                    <DialogPanel
                        class="inline-block max-w-2xl transform overflow-hidden rounded-lg bg-white p-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:p-6 sm:align-middle"
                        :class="{
                            'h-[calc(100vh-50px)] sm:w-[calc(100vw-40px)]': props.fullscreen,
                            'sm:my-8 sm:w-full': !props.fullscreen,
                            'sm:max-w-lg': props.size === 'sm' && !props.fullscreen,
                            'sm:max-w-3xl': props.size === 'md' && !props.fullscreen,
                            'sm:max-w-6xl': props.size === 'lg' && !props.fullscreen,
                            'sm:max-w-7xl': props.size === 'xl' && !props.fullscreen,
                        }">
                        <div v-if="props.showClose" class="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                            <button
                                type="button"
                                class="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                                @click="open = false">
                                <span class="sr-only">Close</span>
                                <XIcon class="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        <slot></slot>
                    </DialogPanel>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>
